// ============================================================
//  LOGIN PAGE — ADMIN DASHBOARD
//  Path: src/pages/LoginPage.tsx
//  Includes forgot password flow
// ============================================================

import React, { useState } from 'react'
import { sendPasswordResetEmail } from 'firebase/auth'
import { auth } from '../config/firebase'
import { useAuth } from '../context/AuthContext'
import { useNavigate, useLocation } from 'react-router-dom'

const LoginPage: React.FC = () => {
  const [email, setEmail]             = useState('')
  const [password, setPassword]       = useState('')
  const [error, setError]             = useState('')
  const [successMsg, setSuccessMsg]   = useState('')
  const [isLoading, setIsLoading]     = useState(false)
  const [showForgot, setShowForgot]   = useState(false)
  const [resetEmail, setResetEmail]   = useState('')
  const [resetSending, setResetSending] = useState(false)
  // ── Authenticator-app code step ──────────────────────────────
  // login() throws AdminLoginError('mfa-required', …) — not a normal
  // failure — when the account has a TOTP factor enrolled. mfaRequired
  // flips true on that and this page swaps to the code form below
  // instead of showing an error. mfaCode is cleared and this input
  // refocused (autoFocus) on every arrival so the admin can go
  // straight from their authenticator app to typing digits.
  const [mfaCode, setMfaCode]         = useState('')
  const [mfaSubmitting, setMfaSubmitting] = useState(false)
  const { login, mfaRequired, completeMfaSignIn, cancelMfaSignIn } = useAuth()
  const navigate  = useNavigate()
  const location  = useLocation()
  // Where ProtectedRoute (App.tsx) sent us from, so a link to e.g.
  // /experts/abc still lands there after signing in. Only an in-app
  // path is accepted — never a full URL.
  const fromState = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from
  const returnTo  = fromState?.pathname && fromState.pathname.startsWith('/') && !fromState.pathname.startsWith('//') && fromState.pathname !== '/login'
    ? fromState.pathname + (fromState.search || '')
    : '/'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccessMsg('')
    setIsLoading(true)

    try {
      await login(email, password)
      navigate(returnTo, { replace: true })
    } catch (err: any) {
      const code = err.code
      if (code === 'mfa-required') {
        // Not an error — login() has stashed the pending sign-in and
        // is waiting on handleMfaSubmit below. Password was correct;
        // nothing needs to be shown here except the code form, which
        // renders because mfaRequired (from context) is now true.
        setPassword('')
      } else if (code === 'not-admin') {
        setError('This account does not have admin access. Contact your administrator.')
      } else if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError('Invalid email or password')
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts — please try again later')
      } else if (code === 'auth/user-disabled') {
        setError('This account has been disabled. Contact your administrator.')
      } else if (err.isNetworkError || code === 'auth/network-request-failed') {
        // isNetworkError = our API (axios); auth/network-request-failed =
        // Firebase itself couldn't be reached during sign-in.
        setError('Could not reach the server — check your connection and try again')
      } else {
        setError('Login failed — please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Firebase keeps a pending second-factor sign-in open for a few
  // minutes only. After that the resolver is dead and retrying the code
  // can never work — these codes mean "start again from the password".
  const MFA_SESSION_GONE = new Set([
    'auth/invalid-multi-factor-session',
    'auth/missing-multi-factor-session',
    'auth/code-expired',
    'no-mfa-challenge', // AuthContext: no pending sign-in at all
  ])

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!mfaCode.trim()) return
    setMfaSubmitting(true)
    try {
      await completeMfaSignIn(mfaCode.trim())
      navigate(returnTo, { replace: true })
    } catch (err: any) {
      // FIX: every failure other than a wrong code used to say "Could not
      // verify that code — try again", including a timed-out sign-in
      // (retrying can never work — the admin was stuck on this screen)
      // and a correct code on a non-admin account (AuthContext's
      // fetchAdminProfile throws 'not-admin' AFTER the code was accepted).
      const code = err?.code
      if (code === 'auth/invalid-verification-code') {
        setError('That code is incorrect or has expired — check the time on your device and try the next code.')
      } else if (MFA_SESSION_GONE.has(code)) {
        cancelMfaSignIn()
        setError('Your sign-in timed out. Please enter your email and password again.')
      } else if (code === 'not-admin') {
        // The code was right; the account just isn't an admin.
        // AuthContext has already signed it out and left the code step.
        setError('This account does not have admin access. Contact your administrator.')
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts — please wait a few minutes and try again.')
      } else if (code === 'auth/user-disabled') {
        cancelMfaSignIn()
        setError('This account has been disabled. Contact your administrator.')
      } else if (err?.isNetworkError || code === 'auth/network-request-failed') {
        setError('Could not reach the server — check your connection and try again')
      } else {
        setError('Could not verify that code — try again.')
      }
      setMfaCode('')
    } finally {
      setMfaSubmitting(false)
    }
  }

  const handleCancelMfa = () => {
    cancelMfaSignIn()
    setMfaCode('')
    setError('')
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccessMsg('')

    if (!resetEmail?.trim()) {
      setError('Please enter your email')
      return
    }

    setResetSending(true)
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim())
      setSuccessMsg('Password reset email sent! Check your inbox.')
      setShowForgot(false)
      setResetEmail('')
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email')
      } else {
        setError('Failed to send reset email — try again')
      }
    } finally {
      setResetSending(false)
    }
  }

  return (
    <div className="min-h-screen bg-surface-primary flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-brand-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🛡️</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-gray-400 mt-2">Sign in to manage your platform</p>
        </div>

        {/* Form */}
        <div className="bg-surface-card border border-border-light rounded-2xl p-8">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          {successMsg && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-6">
              <p className="text-green-400 text-sm">{successMsg}</p>
            </div>
          )}

          {mfaRequired ? (
            /* Authenticator-app code step — shown once login() has
               confirmed the password and Firebase is now waiting on
               the second factor. Cancel goes back to a plain
               email/password form rather than leaving the admin stuck
               if they don't have their device handy. */
            <>
              <h2 className="text-lg font-semibold text-white mb-2">Enter your authenticator code</h2>
              <p className="text-sm text-gray-400 mb-6">Open your authenticator app and enter the 6-digit code for this account.</p>
              <form onSubmit={handleMfaSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Code</label>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" autoFocus
                    value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456" required maxLength={6}
                    className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white text-center text-2xl tracking-[0.5em] placeholder-gray-600 focus:border-brand-500 focus:outline-none transition-colors" />
                </div>
                <button type="submit" disabled={mfaSubmitting || mfaCode.length !== 6}
                  className="w-full bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white font-semibold rounded-xl px-4 py-3 transition-colors mb-3">
                  {mfaSubmitting ? 'Verifying...' : 'Verify & Sign In'}
                </button>
                <button type="button" onClick={handleCancelMfa}
                  className="w-full text-gray-400 hover:text-white text-sm py-2 transition-colors">
                  ← Back to login
                </button>
              </form>
            </>
          ) : !showForgot ? (
            /* Login Form */
            <>
              <form onSubmit={handleSubmit}>
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com" required
                    className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none transition-colors" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" required
                    className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none transition-colors" />
                </div>
                <div className="flex justify-end mb-6">
                  <button type="button" onClick={() => { setShowForgot(true); setError(''); setSuccessMsg('') }}
                    className="text-sm text-brand-400 hover:text-brand-300 transition-colors">
                    Forgot password?
                  </button>
                </div>
                <button type="submit" disabled={isLoading}
                  className="w-full bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white font-semibold rounded-xl px-4 py-3 transition-colors">
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </>
          ) : (
            /* Forgot Password Form */
            <>
              <h2 className="text-lg font-semibold text-white mb-2">Reset Password</h2>
              <p className="text-sm text-gray-400 mb-6">Enter your email and we'll send you a reset link</p>
              <form onSubmit={handleForgotPassword}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="admin@example.com" required autoFocus
                    className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none transition-colors" />
                </div>
                <button type="submit" disabled={resetSending}
                  className="w-full bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white font-semibold rounded-xl px-4 py-3 transition-colors mb-3">
                  {resetSending ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => { setShowForgot(false); setError('') }}
                  className="w-full text-gray-400 hover:text-white text-sm py-2 transition-colors">
                  ← Back to login
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          Contact your administrator if you need access
        </p>
      </div>
    </div>
  )
}

export default LoginPage